import { useEffect, useMemo, useState } from 'react'

import { FilterQuery, SkeletonDots, Text } from '@yes.technology/react-toolkit'
import { isEmpty } from 'lodash'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { GridContainer } from 'shared/style/grid'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import useMergedFilterQuery from 'site/shared/hooks/useMergedFilterQuery'
import { useSiteState } from 'siteState/shared'
import { useFetchYesObjects } from 'yesObject/shared'
import AppliedFilters, {
  AppliedFilterFieldValuesProps
} from '../AppliedFilters/AppliedFilters'

import { AnimationWrapper } from './Cockpit.styles'
import { ChartProps, CockpitProps, FullChartProps } from './Cockpit.types'
import ChartWrapper from './components/ChartWrapper'
import Sections from './components/Sections'
import { useClassifiedCharts } from './hooks'
import useCockpitSectionTransitionMinHeight from './hooks/useCockpitSectionTransitionMinHeight'
import getSectionTitle from './utils/getSectionTitle'

const Cockpit = ({ props = { data: [], titles: [] } }: CockpitProps) => {
  const [section, setSection] = useState(0)
  const [direction, setDirection] = useState<'next' | 'prev'>('next')

  const {
    data = [],
    titles = [],
    applied_filters = [],
    uuid_classification = '',
    uuid_objectclass: objectclassUuid = '',
    styleMap = {},
    merge_filter_query: manifestFilterQuery = {},
    show_applied_filters: showAppliedFilters = true
  } = props

  const [filterQuery] = useSiteState<FilterQuery>('filter-query')
  const [filterExecutionTime] = useSiteState<Date | undefined>(
    'filter-execution-time'
  )

  const mergedFilterQuery = useMergedFilterQuery({
    filterQuery,
    manifestFilterQuery
  })

  const hasValidMergedFilterQuery =
    !!mergedFilterQuery && !isEmpty(mergedFilterQuery)

  const { yesObjects, isFetching: isLoadingObjects } =
    useFetchYesObjects<FullChartProps>({
      filterBody: {
        query: mergedFilterQuery as FilterQuery,
        objectclass: objectclassUuid,
        skip_metadata: true,
        project_fields: {
          uuid: 1,
          des: 1,
          template: 1,
          dataset: 1
        }
      },
      enabled: !!objectclassUuid && hasValidMergedFilterQuery,
      initialPagination: {
        limit: 99999,
        offset: 0
      }
    })

  useEffect(() => {
    if (!filterExecutionTime) return
    setSection(0)
  }, [filterExecutionTime])

  const cockpitData = useMemo(() => {
    if (hasValidMergedFilterQuery && yesObjects && yesObjects?.length > 0) {
      return yesObjects
    }
    return data
  }, [data, yesObjects, hasValidMergedFilterQuery])

  const [dynamicAppliedFilters] = useSiteState<AppliedFilterFieldValuesProps[]>(
    'cockpit-applied-filters'
  )

  const getAppliedFilters = (): AppliedFilterFieldValuesProps[] => {
    try {
      if (dynamicAppliedFilters !== undefined) {
        return dynamicAppliedFilters
      }
      if (applied_filters?.length > 0) {
        return JSON.parse(applied_filters[0].des)
      }
      return []
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return []
    }
  }

  const appliedFilters: AppliedFilterFieldValuesProps[] = getAppliedFilters()

  const styleProps = useStyleMaps(styleMap)

  const {
    transitionRef,
    animationWrapperStyle,
    reserveHeightBeforeSectionChange,
    reserveHeightOnTransitionEntered
  } = useCockpitSectionTransitionMinHeight({
    filterExecutionTime,
    uuid_classification,
    objectclassUuid
  })

  const handleNavigateSections = (index: number) => {
    const newSection = index - 1

    reserveHeightBeforeSectionChange()

    setDirection(newSection > section ? 'next' : 'prev')
    setSection(newSection)
  }

  const { classifiedCharts, sectionsUuids, isLoading } = useClassifiedCharts({
    charts: cockpitData,
    classificationUuid: uuid_classification,
    filterEmptyCharts: yesObjects?.length > 0
  })

  const hasCockpitCharts = cockpitData.length > 0

  const showFullPageLoading =
    !hasCockpitCharts && (isLoadingObjects || isLoading)

  const sectionTitle = getSectionTitle({
    sectionUuid: sectionsUuids[section],
    titles
  })

  if (showFullPageLoading)
    return (
      <div className='p-4'>
        <SkeletonDots />
      </div>
    )

  const rows = (classifiedCharts[section] ?? []) as ChartProps[]

  return (
    <Sections
      onNavigate={handleNavigateSections}
      currentSection={section + 1}
      totalSections={classifiedCharts.length}
      style={styleProps}
      title={sectionTitle}
    >
      {sectionTitle && (
        <div className='d-flex justify-content-center mt-3'>
          <Text variant='heading-emphasis-01' as='h1'>
            {sectionTitle}
          </Text>
        </div>
      )}
      {rows.length > 0 && (
        <AnimationWrapper $direction={direction} style={animationWrapperStyle}>
          <SwitchTransition mode='out-in'>
            <CSSTransition
              key={section}
              nodeRef={transitionRef}
              classNames='fade'
              timeout={300}
              onEntered={reserveHeightOnTransitionEntered}
            >
              <GridContainer ref={transitionRef}>
                <div className={'row mt-3'}>
                  {rows.map(
                    (chart) =>
                      chart && (
                        <ChartWrapper
                          key={chart.uuid}
                          dataset={chart.dataset}
                          des={chart.des}
                          template={chart.template}
                          uuid={chart.uuid}
                          column={chart.column}
                        />
                      )
                  )}
                </div>
                {showAppliedFilters && (
                  <div className='row mb-3'>
                    <div className='col-md-12'>
                      <AppliedFilters fieldValues={appliedFilters} />
                    </div>
                  </div>
                )}
              </GridContainer>
            </CSSTransition>
          </SwitchTransition>
        </AnimationWrapper>
      )}
    </Sections>
  )
}

export default Cockpit
